/* eslint-disable react-hooks/refs */
import { useCallback, useRef, type MutableRefObject } from "react";
import { useQuery } from "urql";
import { ProjectListAndStatusesQry } from "api/queries";
import type { ResultOf } from "@graphql-typed-document-node/core";

type QueryResult = ResultOf<typeof ProjectListAndStatusesQry>;
type ProjectStatus = NonNullable<QueryResult["projectStatuses"]>[number];
type ProjectItem = NonNullable<
  NonNullable<NonNullable<QueryResult["currentUser"]>["projectList"]>["items"]
>[number];
type ProjectListData = NonNullable<
  NonNullable<QueryResult["currentUser"]>["projectList"]
>;

type PersistedData = {
  projectsCount: number;
  pagesCount: number;
  filteredCount: number;
  statistics: NonNullable<ProjectListData["statistics"]>;
  overallStatistics: NonNullable<ProjectListData["overallStatistics"]>;
};

type StatusColorsRecord = Record<string, string>;

type UseProjectListQueryReturn = {
  data: {
    overallStatistics:
      | NonNullable<ProjectListData["overallStatistics"]>
      | undefined;
    statistics: NonNullable<ProjectListData["statistics"]> | undefined;
    projectsCount: number;
    pagesCount: number;
    statusColors: StatusColorsRecord | null;
    filteredCount: number;
    projects: ProjectItem[];
    projectStatuses: ProjectStatus[];
  };
  isLoading: boolean;
  error: Error | null;
  refetch: (opts?: Record<string, unknown>) => void;
};

type UseProjectListQueryArgs = {
  variables: Record<string, unknown>;
  pause?: boolean;
};

export const useProjectListQuery = ({
  variables,
  pause = false,
}: UseProjectListQueryArgs): UseProjectListQueryReturn => {
  const [{ data, error, fetching, stale }, refetch] = useQuery<QueryResult>({
    query: ProjectListAndStatusesQry,
    variables,
    pause,
  });

  const projectStatuses = data?.projectStatuses || [];
  const projectList = data?.currentUser?.projectList;

  const projectsCount =
    projectList?.overallStatistics &&
    (projectList.overallStatistics.shared || 0) +
      (projectList.overallStatistics.notShared || 0)
      ? Number(projectList.overallStatistics.shared) +
        Number(projectList.overallStatistics.notShared)
      : 0;

  const pagesCount = projectList?.pages || 0;

  const isLoading = fetching || stale;

  const getStatusColors = useCallback((statuses: ProjectStatus[]) => {
    if (statuses && statuses.length > 0) {
      return statuses.reduce<StatusColorsRecord>((result, current) => {
        if (current) {
          result[current.status] = current.color;
        }
        return result;
      }, {});
    }
    return {};
  }, []);

  const persistedDataRef: MutableRefObject<PersistedData | null> = useRef(null);
  if (!isLoading && !error && projectList) {
    persistedDataRef.current = {
      projectsCount: projectsCount || 0,
      pagesCount: pagesCount || 0,
      filteredCount: projectList.projectsCount || 0,
      statistics: projectList.statistics as NonNullable<
        ProjectListData["statistics"]
      >,
      overallStatistics: projectList.overallStatistics as NonNullable<
        ProjectListData["overallStatistics"]
      >,
    };
  }

  const statusColorsRef: MutableRefObject<StatusColorsRecord | null> =
    useRef(null);
  if (
    projectStatuses &&
    projectStatuses.length > 0 &&
    !statusColorsRef.current
  ) {
    statusColorsRef.current = getStatusColors(projectStatuses);
  }

  if (error) {
    console.error("useProjectList error: ", error);
  }

  return {
    data: {
      overallStatistics: persistedDataRef.current?.overallStatistics,
      statistics: persistedDataRef.current?.statistics,
      projectsCount: persistedDataRef.current?.projectsCount || 0,
      pagesCount: persistedDataRef.current?.pagesCount || 0,
      statusColors: statusColorsRef.current,
      filteredCount: persistedDataRef.current?.filteredCount || 0,
      projects: (projectList?.items as ProjectItem[]) || [],
      projectStatuses: (projectStatuses as ProjectStatus[]) || [],
    },
    isLoading: fetching || stale,
    error: (error as Error) || null,
    refetch,
  };
};
